import React, { useMemo } from "react";
import { Spin, Alert, Card, Row, Col, Typography } from "antd";
import { useTranslation } from "react-i18next";
import RoleWidgets from "../components/RoleWidgets";
import useDashboardData from "../hooks/useDashboarData";
import { useSelector } from "react-redux";
import { useEffectiveStudentId } from "../../../components/Peoples/guardian/hooks/useEffectiveStudentId";
import StudentSelectorWrapper from "../../../components/Peoples/guardian/StudentSelectorWrapper";
import ImageWithBasePath from "../../../components/Common/imageWithBasePath";
import { tips } from "../../../core/data/tips";
import { salutes } from "../../../core/data/salutes";

// Utility to pick a daily key from an array based on day-of-year
const getDailyKey = <T,>(arr: T[]): T => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0).getTime();
  const diff = now.getTime() - start;
  const day = Math.floor(diff / (1000 * 60 * 60 * 24));
  return arr[day % arr.length];
};

const { Title } = Typography;

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const role = useSelector((state: any) => state.authSlice.user?.role);
  const name = useSelector((state: any) => state.authSlice.user?.name);
  const studentId = useEffectiveStudentId();

  // Fetch dashboard data
  const { data, isLoading, error } = useDashboardData();

  const tipKey = useMemo(() => getDailyKey(tips), []);

  // Format user name to Title Case
  const formatName = (rawName: string = "") =>
    rawName
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');

  // Pick daily salute based on day of year
  const saluteKey = useMemo(() => getDailyKey(salutes), []);

  return (
    <div className="page-wrapper">
        <div className="content">
          <div className="min-vh-100">
          {/* Dashboard Title */}
          <Row justify="space-between" align="middle" className="mb-4">
            <Col>
              <h1>{t("dashboard")}</h1>
            </Col>
          </Row>

          {/* Banner Section */}
          <div className="card bg-primary mb-4">
            <div className="overlay-img">
              <ImageWithBasePath className="img-fluid shape-01" alt="img" src="assets/img/bg/shape-04.png" />
              <ImageWithBasePath className="img-fluid shape-02" alt="img" src="assets/img/bg/shape-01.png" />
              <ImageWithBasePath className="img-fluid shape-03" alt="img" src="assets/img/bg/shape-02.png" />
              <ImageWithBasePath className="img-fluid shape-04" alt="img" src="assets/img/bg/shape-03.png" />
            </div>
            <div className="card-body">
              <div className="d-flex align-items-xl-center justify-content-xl-between flex-xl-row flex-column">
                <div className="mb-3 mb-xl-0">
                  <div className="d-flex align-items-center flex-wrap mb-2">
                    <h1 className="text-white me-2">
                      {t("dashboard.welcomeBack", { name: formatName(name) })}
                    </h1>
                  </div>
                  <p className="text-white">{t(saluteKey)}</p>
                </div>
                <p className="text-white custom-text-white">
                  <i className="ti ti-star me-1"></i>{t("dashboard.tipOfDay")} “{t(tipKey)}”
                </p>
              </div>
            </div>
          </div>

          {/* Student Selector (only if guardian) */}
          <StudentSelectorWrapper>
            {role === "guardian" && !studentId ? (
              <p className="text-danger">{t("select_student_first")}</p>
            ) : (
              <>
                {/* Dashboard Widgets Section */}
                <Row justify="center">
                  <Col xs={24} sm={24} md={20} lg={24}>
                    {isLoading && <Spin size="large" className="d-block mx-auto my-3" />}
                    {error && <Alert message={t("failed_to_load_dashboard")} type="error" showIcon className="my-3" />}
                    {data && <RoleWidgets role={role} data={data} />}
                  </Col>
                </Row>
              </>
            )}
          </StudentSelectorWrapper>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
